import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  IconButton,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  LinearProgress,
  Avatar
} from '@mui/material';
import {
  CalendarMonth,
  PictureAsPdf,
  TableChart,
  FilterList,
  Clear,
  AttachMoney,
  Assessment,
  AccountBalance,
  CreditCard,
  TrendingUp,
  TrendingDown,
  Visibility,
  CalendarToday,
  ShowChart,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  Search
} from '@mui/icons-material';
import {
  ComposedChart,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';
import HttpsIcon from '@mui/icons-material/Https';
import { DatePicker, message, Input, ConfigProvider } from 'antd';
import moment from 'moment';
import 'moment/locale/es';
import esES from 'antd/locale/es_ES';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

import LoadingInformation from '../../Components/LoadingInformation';

// Configurar moment en español
moment.locale('es');

const { RangePicker } = DatePicker;

function parseNumber(str) {
  if (!str) return 0;
  return Number(str.toString().replace(/,/g, ''));
}

function formatCurrency(value) {
  if (value === null || value === undefined) return '0.00';
  let numValue;
  if (typeof value === 'string') {
    numValue = parseFloat(value.replace(/,/g, ''));
  } else if (typeof value === 'number') {
    numValue = value;
  } else {
    numValue = 0;
  }
  return numValue.toLocaleString('es-DO', {
    style: 'currency',
    currency: 'DOP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

const COLORS = ['#f759ab', '#22c55e', '#3b82f6', '#f97316', '#8b5cf6', '#06b6d4', 
                '#ef4444', '#10b981', '#6366f1', '#f59e0b'];

const formatDate = () => {
  const fecha = new Date();
  return new Intl.DateTimeFormat("es-ES", { year: "numeric" }).format(fecha);
};

function AnnualPaymentReport() {
  const [payments, setPayments] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();
  const [search, setSearch] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('todos');
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [selectedQuarter, setSelectedQuarter] = useState('todos');
  const [selectedMonth, setSelectedMonth] = useState('todos');
  const [dateRange, setDateRange] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [permissionError, setPermissionError] = useState(false);

  // Summary metrics ampliadas para análisis anual
  const [summary, setSummary] = useState({
    totalPayments: 0,
    totalAmount: 0,
    averagePayment: 0,
    transferAmount: 0,
    cashAmount: 0,
    cardAmount: 0,
    chequeAmount: 0,
    paymentsByMethod: {},
    monthlyTotals: [],
    quarterlyTotals: [],
    weeklyAverage: 0,
    monthlyAverage: 0,
    bestMonth: '',
    bestMonthAmount: 0,
    worstMonth: '',
    worstMonthAmount: 0,
    bestQuarter: '',
    bestQuarterAmount: 0,
    largestPayment: 0,
    smallestPayment: 0,
    growth: 0,
    yearOverYearGrowth: 0,
    peakDays: [],
    seasonalTrends: {},
    clientDistribution: {},
    employeePerformance: {}
  });

  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const currentYear = moment().year();

  // Generar años disponibles (últimos 5 años + año actual)
  const availableYears = Array.from({ length: 6 }, (_, i) => currentYear - i);

  // Configurar las fechas permitidas (solo del año seleccionado)
  const disabledDate = (current) => {
    if (!current) return false;
    return !current.isSame(selectedYear, 'year');
  };

  const getPayments = async () => {
    setLoading(true);
    setPermissionError(false);
    try {
      const response = await fetch(`https://dprincess-training.servehttp.com/payments/year/${selectedYear}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 403) {
        setPermissionError(true);
        setLoading(false);
        return;
      }
      if (!response.ok) {
        throw new Error('Error al obtener los pagos');
      }

      const data = await response.json();

      const formattedPayments = data.map(payment => ({
        id: payment.transactionID,
        invoiceID: payment.invoiceID,
        invoiceNumber: payment.invoiceNumber || `${payment.invoiceID}`,
        clientName: payment.clientName || 'Cliente no especificado',
        amount: parseNumber(payment.totalAmountReceived),
        paymentMethod: (payment.paymentMethod || 'efectivo').toLowerCase(),
        paymentDate: payment.transactionDate,
        description: payment.description || payment.notes || '',
        employeeName: payment.employeeName || 'No especificado',
        createdAt: payment.createdAt
      }));

      setPayments(formattedPayments);
      setFilteredPayments(formattedPayments);

    } catch (error) {
      console.error('Error al obtener pagos:', error);
      messageApi.error('Error al cargar los datos de pagos');
    } finally {
      setLoading(false);
    }
  };

  const calculateAdvancedSummary = (paymentData) => {
    const totalPayments = paymentData.length;
    const totalAmount = paymentData.reduce((sum, payment) => sum + payment.amount, 0);
    const averagePayment = totalPayments > 0 ? totalAmount / totalPayments : 0;

    // Calcular métricas por método de pago
    const transferAmount = paymentData
      .filter(payment => payment.paymentMethod === 'transferencia')
      .reduce((sum, payment) => sum + payment.amount, 0);

    const cashAmount = paymentData
      .filter(payment => payment.paymentMethod === 'efectivo')
      .reduce((sum, payment) => sum + payment.amount, 0);

    const cardAmount = paymentData
      .filter(payment => payment.paymentMethod === 'tarjeta')
      .reduce((sum, payment) => sum + payment.amount, 0);

    const chequeAmount = paymentData
      .filter(payment => payment.paymentMethod === 'cheque')
      .reduce((sum, payment) => sum + payment.amount, 0);

    // Pagos más alto y más bajo
    const amounts = paymentData.map(p => p.amount).filter(a => a > 0);
    const largestPayment = amounts.length > 0 ? Math.max(...amounts) : 0;
    const smallestPayment = amounts.length > 0 ? Math.min(...amounts) : 0;

    // Análisis mensual - usando números de mes para mayor precisión
    const monthlyTotals = {};
    for (let month = 0; month < 12; month++) {
      const monthKey = moment().month(month).format('MMMM');
      monthlyTotals[monthKey] = {
        amount: 0,
        count: 0,
        month: month + 1
      };
    }

    paymentData.forEach(payment => {
      const paymentMonth = moment(payment.paymentDate).format('MMMM');
      if (monthlyTotals[paymentMonth]) {
        monthlyTotals[paymentMonth].amount += payment.amount;
        monthlyTotals[paymentMonth].count += 1;
      }
    });

    const monthlyArray = Object.entries(monthlyTotals).map(([month, data]) => ({
      month,
      amount: data.amount,
      count: data.count
    }));

    // Mejor y peor mes
    const bestMonthEntry = monthlyArray.reduce((max, current) =>
      current.amount > max.amount ? current : max, { month: '', amount: 0 }
    );

    const worstMonthEntry = monthlyArray
      .filter(m => m.amount > 0)
      .reduce((min, current) =>
        current.amount < min.amount ? current : min, { month: '', amount: Infinity }
      );

    // Análisis trimestral - usando números de mes para ser más preciso
    const quarterlyTotals = {
      'Q1': { amount: 0, count: 0 },
      'Q2': { amount: 0, count: 0 },
      'Q3': { amount: 0, count: 0 },
      'Q4': { amount: 0, count: 0 }
    };

    // Calcular trimestres directamente desde los datos de pagos
    paymentData.forEach(payment => {
      const paymentMonth = moment(payment.paymentDate).month() + 1; // 1-12
      let quarter;
      
      if (paymentMonth >= 1 && paymentMonth <= 3) {
        quarter = 'Q1';
      } else if (paymentMonth >= 4 && paymentMonth <= 6) {
        quarter = 'Q2';
      } else if (paymentMonth >= 7 && paymentMonth <= 9) {
        quarter = 'Q3';
      } else {
        quarter = 'Q4';
      }
      
      quarterlyTotals[quarter].amount += payment.amount;
      quarterlyTotals[quarter].count += 1;
    });

    const bestQuarterEntry = Object.entries(quarterlyTotals).reduce((max, [quarter, data]) =>
      data.amount > max[1].amount ? [quarter, data] : max, ['', { amount: 0 }]
    );

    // Promedio mensual y semanal
    const monthsWithData = monthlyArray.filter(m => m.amount > 0).length;
    const monthlyAverage = monthsWithData > 0 ? totalAmount / monthsWithData : 0;
    const weeklyAverage = totalAmount / 52; // Aproximación semanal

    // Distribución por cliente (top 10)
    const clientDistribution = {};
    paymentData.forEach(payment => {
      const clientName = payment.clientName || 'Cliente Sin Nombre';
      if (!clientDistribution[clientName]) {
        clientDistribution[clientName] = { amount: 0, count: 0 };
      }
      clientDistribution[clientName].amount += payment.amount;
      clientDistribution[clientName].count += 1;
    });

    // Rendimiento por empleado
    const employeePerformance = {};
    paymentData.forEach(payment => {
      if (!employeePerformance[payment.employeeName]) {
        employeePerformance[payment.employeeName] = { amount: 0, count: 0 };
      }
      employeePerformance[payment.employeeName].amount += payment.amount;
      employeePerformance[payment.employeeName].count += 1;
    });

    // Tendencias estacionales
    const seasonalTrends = {
      'Verano': monthlyTotals['Junio']?.amount + monthlyTotals['Julio']?.amount + monthlyTotals['Agosto']?.amount || 0,
      'Otoño': monthlyTotals['Septiembre']?.amount + monthlyTotals['Octubre']?.amount + monthlyTotals['Noviembre']?.amount || 0,
      'Invierno': monthlyTotals['Diciembre']?.amount + monthlyTotals['Enero']?.amount + monthlyTotals['Febrero']?.amount || 0,
      'Primavera': monthlyTotals['Marzo']?.amount + monthlyTotals['Abril']?.amount + monthlyTotals['Mayo']?.amount || 0
    };

    // Días pico (días con mayor actividad)
    const dailyTotals = {};
    paymentData.forEach(payment => {
      const day = moment(payment.paymentDate).format('YYYY-MM-DD');
      if (!dailyTotals[day]) {
        dailyTotals[day] = { amount: 0, count: 0 };
      }
      dailyTotals[day].amount += payment.amount;
      dailyTotals[day].count += 1;
    });

    const peakDays = Object.entries(dailyTotals)
      .sort(([, a], [, b]) => b.amount - a.amount)
      .slice(0, 5)
      .map(([day, data]) => ({
        date: moment(day).format('DD/MM/YYYY'),
        amount: data.amount,
        count: data.count
      }));

    // Agrupar por método de pago para gráficos
    const paymentsByMethod = {};
    paymentData.forEach(payment => {
      const method = payment.paymentMethod.charAt(0).toUpperCase() + payment.paymentMethod.slice(1);
      if (!paymentsByMethod[method]) {
        paymentsByMethod[method] = 0;
      }
      paymentsByMethod[method] += payment.amount;
    });

    setSummary({
      totalPayments,
      totalAmount,
      averagePayment,
      transferAmount,
      cashAmount,
      cardAmount,
      chequeAmount,
      paymentsByMethod,
      monthlyTotals: monthlyArray,
      quarterlyTotals: Object.entries(quarterlyTotals).map(([quarter, data]) => ({
        quarter,
        amount: data.amount,
        count: data.count
      })),
      weeklyAverage,
      monthlyAverage,
      bestMonth: bestMonthEntry.month,
      bestMonthAmount: bestMonthEntry.amount,
      worstMonth: worstMonthEntry.amount !== Infinity ? worstMonthEntry.month : '',
      worstMonthAmount: worstMonthEntry.amount !== Infinity ? worstMonthEntry.amount : 0,
      bestQuarter: bestQuarterEntry[0],
      bestQuarterAmount: bestQuarterEntry[1].amount,
      largestPayment,
      smallestPayment,
      growth: 0, // Se calcularía comparando con año anterior
      yearOverYearGrowth: 0, // Se calcularía con datos históricos
      peakDays,
      seasonalTrends,
      clientDistribution,
      employeePerformance
    });
  };

  const applyFilters = useCallback(() => {
    let filtered = [...payments];

    // Filtrar por rango de fechas
    if (dateRange && dateRange.length === 2) {
      const startDate = dateRange[0].startOf('day');
      const endDate = dateRange[1].endOf('day');
      filtered = filtered.filter(payment => {
        const paymentDate = moment(payment.paymentDate);
        return paymentDate.isBetween(startDate, endDate, null, '[]');
      });
    }

    // Filtrar por trimestre
    if (selectedQuarter !== 'todos') {
      const quarterMonths = {
        'Q1': [1, 2, 3],
        'Q2': [4, 5, 6],
        'Q3': [7, 8, 9],
        'Q4': [10, 11, 12]
      };
      filtered = filtered.filter(payment => {
        const month = moment(payment.paymentDate).month() + 1;
        return quarterMonths[selectedQuarter].includes(month);
      });
    }

    // Filtrar por mes específico
    if (selectedMonth !== 'todos') {
      filtered = filtered.filter(payment => {
        const month = moment(payment.paymentDate).month() + 1;
        return month === parseInt(selectedMonth);
      });
    }

    // Filtrar por método de pago
    if (paymentMethod !== 'todos') {
      filtered = filtered.filter(payment =>
        payment.paymentMethod.toLowerCase() === paymentMethod.toLowerCase()
      );
    }

    // Filtrar por término de búsqueda
    if (search.trim()) {
      const searchTerm = search.toLowerCase().trim();
      filtered = filtered.filter(payment =>
        payment.invoiceNumber.toLowerCase().includes(searchTerm) ||
        payment.clientName.toLowerCase().includes(searchTerm) ||
        payment.description.toLowerCase().includes(searchTerm) ||
        payment.employeeName.toLowerCase().includes(searchTerm)
      );
    }

    setFilteredPayments(filtered);
    calculateAdvancedSummary(filtered);
  }, [payments, search, paymentMethod, selectedQuarter, selectedMonth, dateRange]);

  const clearAllFilters = () => {
    setDateRange(null);
    setPaymentMethod('todos');
    setSelectedQuarter('todos');
    setSelectedMonth('todos');
    setSearch('');
  };

  const prepareChartData = () => {
    // Verificar si summary tiene datos válidos
    if (!summary || !summary.clientDistribution) {
      return {
        monthly: [],
        quarterly: [],
        methods: [],
        seasonal: [],
        topClients: []
      };
    }

    // Datos mensuales
    const monthlyData = summary.monthlyTotals.map(month => ({
      month: month.month.substring(0, 3),
      amount: month.amount,
      count: month.count,
      average: month.count > 0 ? month.amount / month.count : 0
    }));

    // Datos trimestrales
    const quarterlyData = summary.quarterlyTotals.map(quarter => ({
      quarter: quarter.quarter,
      amount: quarter.amount,
      count: quarter.count,
      percentage: summary.totalAmount > 0 ? (quarter.amount / summary.totalAmount) * 100 : 0
    }));

    // Datos por método de pago
    const methodsData = Object.entries(summary.paymentsByMethod).map(([method, amount], index) => ({
      method,
      amount,
      percentage: summary.totalAmount > 0 ? (amount / summary.totalAmount) * 100 : 0,
      color: COLORS[index % COLORS.length]
    }));

    // Datos estacionales
    const seasonalData = Object.entries(summary.seasonalTrends).map(([season, amount], index) => ({
      season,
      amount,
      percentage: summary.totalAmount > 0 ? (amount / summary.totalAmount) * 100 : 0,
      color: COLORS[index % COLORS.length]
    }));

    // Top clientes - con logging para debug
    const clientEntries = Object.entries(summary.clientDistribution || {});
    
    const topClients = clientEntries
      .filter(([client, data]) => {
        const isValid = client && client.trim() !== '' && data && data.amount > 0;
        return isValid;
      })
      .sort(([, a], [, b]) => b.amount - a.amount)
      .slice(0, 10)
      .map(([client, data]) => ({
        client: client.length > 15 ? client.substring(0, 15) + '...' : client,
        amount: data.amount,
        count: data.count
      }));

    return {
      monthly: monthlyData,
      quarterly: quarterlyData,
      methods: methodsData,
      seasonal: seasonalData,
      topClients
    };
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const pageHeight = doc.internal.pageSize.height;
    let currentY = 20;

    // Título
    doc.setFontSize(20);
    doc.setTextColor(75, 0, 130);
    doc.text(`Reporte Anual de Pagos ${selectedYear}`, 20, currentY);
    currentY += 15;

    // Resumen ejecutivo
    doc.setFontSize(14);
    doc.setTextColor(75, 0, 130);
    doc.text('Resumen Ejecutivo', 20, currentY);
    currentY += 10;

    doc.setFontSize(10);
    const summaryData = [
      ['Total de Pagos', summary.totalPayments.toString()],
      ['Monto Total', formatCurrency(summary.totalAmount)],
      ['Promedio por Pago', formatCurrency(summary.averagePayment)],
      ['Promedio Mensual', formatCurrency(summary.monthlyAverage)],
      ['Mejor Mes', `${summary.bestMonth} - ${formatCurrency(summary.bestMonthAmount)}`],
      ['Mejor Trimestre', `${summary.bestQuarter} - ${formatCurrency(summary.bestQuarterAmount)}`],
      ['Pago Mayor', formatCurrency(summary.largestPayment)],
      ['Pago Menor', formatCurrency(summary.smallestPayment)]
    ];

    autoTable(doc, {
      startY: currentY,
      head: [['Métrica', 'Valor']],
      body: summaryData,
      theme: 'grid',
      headStyles: { 
        fillColor: [247, 89, 171],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      bodyStyles: {
        textColor: [75, 0, 130]
      },
      margin: { left: 20, right: 20 }
    });

    currentY = doc.lastAutoTable.finalY + 15;

    // Verificar si necesitamos nueva página
    if (currentY > pageHeight - 60) {
      doc.addPage();
      currentY = 20;
    }

    // Tabla de análisis mensual
    doc.setFontSize(14);
    doc.setTextColor(75, 0, 130);
    doc.text('Análisis Mensual', 20, currentY);
    currentY += 10;

    const monthlyData = summary.monthlyTotals.map(month => [
      month.month,
      month.count.toString(),
      formatCurrency(month.amount),
      formatCurrency(month.count > 0 ? month.amount / month.count : 0)
    ]);

    autoTable(doc, {
      startY: currentY,
      head: [['Mes', 'Cantidad', 'Monto Total', 'Promedio']],
      body: monthlyData,
      theme: 'striped',
      headStyles: { 
        fillColor: [75, 0, 130],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      alternateRowStyles: {
        fillColor: [248, 240, 252]
      },
      margin: { left: 20, right: 20 }
    });

    currentY = doc.lastAutoTable.finalY + 15;

    // Verificar si necesitamos nueva página para métodos de pago
    if (currentY > pageHeight - 80) {
      doc.addPage();
      currentY = 20;
    }

    // Tabla de métodos de pago
    doc.setFontSize(14);
    doc.setTextColor(75, 0, 130);
    doc.text('Métodos de Pago', 20, currentY);
    currentY += 10;

    const methodsData = Object.entries(summary.paymentsByMethod).map(([method, amount]) => [
      method.charAt(0).toUpperCase() + method.slice(1),
      formatCurrency(amount),
      `${((amount / summary.totalAmount) * 100).toFixed(2)}%`
    ]);

    autoTable(doc, {
      startY: currentY,
      head: [['Método', 'Monto', 'Porcentaje']],
      body: methodsData,
      theme: 'grid',
      headStyles: { 
        fillColor: [139, 92, 246],
        textColor: [255, 255, 255],
        fontStyle: 'bold'
      },
      margin: { left: 20, right: 20 }
    });

    // Pie de página con fecha
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(
        `Generado el ${moment().format('DD/MM/YYYY HH:mm')} - Página ${i} de ${pageCount}`,
        20,
        pageHeight - 10
      );
    }

    doc.save(`reporte-anual-pagos-${selectedYear}.pdf`);
    messageApi.success('PDF exportado exitosamente');
  };

  const handleExportExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      
      // Configuración del workbook
      workbook.creator = 'Sistema de Pagos';
      workbook.lastModifiedBy = 'Sistema de Pagos';
      workbook.created = new Date();
      workbook.modified = new Date();

      // Hoja de resumen
      const summarySheet = workbook.addWorksheet('Resumen Ejecutivo');
      
      // Configurar columnas
      summarySheet.columns = [
        { header: 'Métrica', key: 'metric', width: 25 },
        { header: 'Valor', key: 'value', width: 20 }
      ];

      // Estilo del header
      summarySheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } };
      summarySheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'F759AB' }
      };

      // Datos del resumen
      const summaryData = [
        { metric: 'Año de Análisis', value: selectedYear.toString() },
        { metric: 'Total de Pagos', value: summary.totalPayments },
        { metric: 'Monto Total', value: summary.totalAmount },
        { metric: 'Promedio por Pago', value: summary.averagePayment },
        { metric: 'Promedio Mensual', value: summary.monthlyAverage },
        { metric: 'Promedio Semanal', value: summary.weeklyAverage },
        { metric: 'Mejor Mes', value: `${summary.bestMonth} (${summary.bestMonthAmount})` },
        { metric: 'Mejor Trimestre', value: `${summary.bestQuarter} (${summary.bestQuarterAmount})` },
        { metric: 'Pago Mayor', value: summary.largestPayment },
        { metric: 'Pago Menor', value: summary.smallestPayment },
        { metric: 'Efectivo Total', value: summary.cashAmount },
        { metric: 'Tarjeta Total', value: summary.cardAmount },
        { metric: 'Transferencia Total', value: summary.transferAmount },
        { metric: 'Cheque Total', value: summary.chequeAmount }
      ];

      summarySheet.addRows(summaryData);

      // Formatear números como moneda
      const currencyRows = [3, 4, 5, 6, 9, 10, 11, 12, 13, 14];
      currencyRows.forEach(rowNum => {
        const cell = summarySheet.getCell(`B${rowNum + 1}`);
        cell.numFmt = '"$"#,##0.00';
      });

      // Hoja de análisis mensual
      const monthlySheet = workbook.addWorksheet('Análisis Mensual');
      
      monthlySheet.columns = [
        { header: 'Mes', key: 'month', width: 15 },
        { header: 'Cantidad de Pagos', key: 'count', width: 18 },
        { header: 'Monto Total', key: 'amount', width: 15 },
        { header: 'Promedio', key: 'average', width: 15 }
      ];

      // Estilo del header mensual
      monthlySheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } };
      monthlySheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '4B0082' }
      };

      // Datos mensuales
      const monthlyData = summary.monthlyTotals.map(month => ({
        month: month.month,
        count: month.count,
        amount: month.amount,
        average: month.count > 0 ? month.amount / month.count : 0
      }));

      monthlySheet.addRows(monthlyData);

      // Formatear columnas de moneda
      monthlySheet.getColumn('C').numFmt = '"$"#,##0.00';
      monthlySheet.getColumn('D').numFmt = '"$"#,##0.00';

      // Hoja de métodos de pago
      const methodsSheet = workbook.addWorksheet('Métodos de Pago');
      
      methodsSheet.columns = [
        { header: 'Método de Pago', key: 'method', width: 20 },
        { header: 'Monto Total', key: 'amount', width: 15 },
        { header: 'Porcentaje', key: 'percentage', width: 12 }
      ];

      // Estilo del header métodos
      methodsSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } };
      methodsSheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '8B5CF6' }
      };

      // Datos de métodos
      const methodsData = Object.entries(summary.paymentsByMethod).map(([method, amount]) => ({
        method: method.charAt(0).toUpperCase() + method.slice(1),
        amount: amount,
        percentage: (amount / summary.totalAmount) * 100
      }));

      methodsSheet.addRows(methodsData);

      // Formatear columnas
      methodsSheet.getColumn('B').numFmt = '"$"#,##0.00';
      methodsSheet.getColumn('C').numFmt = '0.00"%"';

      // Hoja de detalles completos
      const detailsSheet = workbook.addWorksheet('Detalles de Pagos');
      
      detailsSheet.columns = [
        { header: 'Fecha', key: 'date', width: 12 },
        { header: 'Factura', key: 'invoice', width: 15 },
        { header: 'Cliente', key: 'client', width: 25 },
        { header: 'Método', key: 'method', width: 15 },
        { header: 'Monto', key: 'amount', width: 12 },
        { header: 'Descripción', key: 'description', width: 30 },
        { header: 'Empleado', key: 'employee', width: 20 }
      ];

      // Estilo del header detalles
      detailsSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } };
      detailsSheet.getRow(1).fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: '22C55E' }
      };

      // Datos de detalles
      const detailsData = filteredPayments.map(payment => ({
        date: moment(payment.paymentDate).format('DD/MM/YYYY'),
        invoice: payment.invoiceNumber,
        client: payment.clientName,
        method: payment.paymentMethod.toUpperCase(),
        amount: payment.amount,
        description: payment.description || 'N/A',
        employee: payment.employeeName || 'N/A'
      }));

      detailsSheet.addRows(detailsData);

      // Formatear columna de monto
      detailsSheet.getColumn('E').numFmt = '"$"#,##0.00';

      // Hoja de días pico
      if (summary.peakDays && summary.peakDays.length > 0) {
        const peakDaysSheet = workbook.addWorksheet('Días Pico');
        
        peakDaysSheet.columns = [
          { header: 'Ranking', key: 'rank', width: 10 },
          { header: 'Fecha', key: 'date', width: 15 },
          { header: 'Monto Total', key: 'amount', width: 15 },
          { header: 'Cantidad Pagos', key: 'count', width: 15 }
        ];

        // Estilo del header días pico
        peakDaysSheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFF' } };
        peakDaysSheet.getRow(1).fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'F97316' }
        };

        // Datos de días pico
        const peakDaysData = summary.peakDays.map((day, index) => ({
          rank: index + 1,
          date: day.date,
          amount: day.amount,
          count: day.count
        }));

        peakDaysSheet.addRows(peakDaysData);

        // Formatear columna de monto
        peakDaysSheet.getColumn('C').numFmt = '"$"#,##0.00';
      }

      // Generar el archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      saveAs(blob, `reporte-anual-pagos-${selectedYear}.xlsx`);
      messageApi.success('Excel exportado exitosamente');

    } catch (error) {
      console.error('Error al exportar Excel:', error);
      messageApi.error('Error al exportar el archivo Excel');
    }
  };

  useEffect(() => {
    getPayments();
  }, [selectedYear]);

  useEffect(() => {
    applyFilters();
  }, [search, paymentMethod, selectedQuarter, selectedMonth, dateRange, payments, applyFilters]);

  const chartData = prepareChartData();

  if (loading) {
    return <LoadingInformation />;
  }

  // Mostrar mensaje de falta de permisos si corresponde
  if (permissionError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          gap: 2
        }}>
          <Avatar sx={{ bgcolor: '#f44336', width: 64, height: 64 }}>
            {/* Icono de candado para indicar acceso restringido */}
            <HttpsIcon sx={{ fontSize: 40 }} />
          </Avatar>
          <Typography variant="h5" sx={{ color: '#f44336', fontWeight: 700 }}>
            No tienes permiso para acceder a esta sección
          </Typography>
          <Typography variant="body1" sx={{ color: '#666', textAlign: 'center' }}>
            Esta información solo está disponible para usuarios con permisos de administrador.
          </Typography>
        </Box>
      </Container>
    );
  };

  return (
    <ConfigProvider locale={esES}>
      <div style={{ background: '#f3e8ff', minHeight: '100vh', padding: '20px 0' }}>
        <Container maxWidth="xl">
          {contextHolder}

          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Typography
              variant="h2"
              sx={{
                mb: 2,
                color: '#4b0082',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' },
                fontSize: {
                  xs: '1.5rem',
                  sm: '2.125rem',
                  md: '3rem'
                }
              }}
            >
              <CalendarMonth sx={{
                fontSize: { xs: 30, sm: 50 },
                color: '#f759ab'
              }} />
              Reporte Anual de Pagos {selectedYear}
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ mb: 3 }}
            >
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel sx={{ color: '#4b0082' }}>Año</InputLabel>
                <Select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  label="Año"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' }
                  }}
                >
                  {availableYears.map(year => (
                    <MenuItem key={year} value={year}>{year}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                startIcon={<PictureAsPdf />}
                onClick={handleExportPDF}
                variant="contained"
                sx={{
                  backgroundColor: '#f759ab',
                  '&:hover': { backgroundColor: '#e04897' }
                }}
              >
                Exportar PDF
              </Button>
              <Button
                startIcon={<TableChart />}
                onClick={handleExportExcel}
                variant="contained"
                sx={{
                  backgroundColor: '#4b0082',
                  '&:hover': { backgroundColor: '#3730a3' }
                }}
              >
                Exportar Excel
              </Button>
            </Stack>
          </Box>

          {/* Filtros */}
          <Card sx={{ mb: 4, borderRadius: '18px', border: '1.5px solid #f759ab' }}>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  mb: 2,
                  color: '#4b0082',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <FilterList sx={{ mr: 1, color: '#f759ab' }} />
                Filtros de Análisis
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <ConfigProvider theme={{ token: { colorPrimary: '#f759ab' } }}>
                    <Input
                      placeholder="Buscar por factura, cliente o empleado"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      allowClear
                      size="large"
                      prefix={<Search />}
                    />
                  </ConfigProvider>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#4b0082' }}>Trimestre</InputLabel>
                    <Select
                      value={selectedQuarter}
                      onChange={(e) => setSelectedQuarter(e.target.value)}
                      label="Trimestre"
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' }
                      }}
                    >
                      <MenuItem value="todos">Todos</MenuItem>
                      <MenuItem value="Q1">Q1 (Ene-Mar)</MenuItem>
                      <MenuItem value="Q2">Q2 (Abr-Jun)</MenuItem>
                      <MenuItem value="Q3">Q3 (Jul-Sep)</MenuItem>
                      <MenuItem value="Q4">Q4 (Oct-Dic)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#4b0082' }}>Mes</InputLabel>
                    <Select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      label="Mes"
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' }
                      }}
                    >
                      <MenuItem value="todos">Todos</MenuItem>
                      {[
                        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
                      ].map((month, index) => (
                        <MenuItem key={index + 1} value={index + 1}>
                          {month}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={2}>
                  <FormControl fullWidth>
                    <InputLabel sx={{ color: '#4b0082' }}>Método</InputLabel>
                    <Select
                      value={paymentMethod}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                      label="Método"
                      sx={{
                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#f759ab' }
                      }}
                    >
                      <MenuItem value="todos">Todos</MenuItem>
                      <MenuItem value="efectivo">Efectivo</MenuItem>
                      <MenuItem value="tarjeta">Tarjeta</MenuItem>
                      <MenuItem value="transferencia">Transferencia</MenuItem>
                      <MenuItem value="cheque">Cheque</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <ConfigProvider theme={{ token: { colorPrimary: '#f759ab' } }}>
                      <RangePicker
                        value={dateRange}
                        onChange={setDateRange}
                        disabledDate={disabledDate}
                        placeholder={['Fecha inicio', 'Fecha fin']}
                        format="DD/MM/YYYY"
                        size="large"
                        style={{ width: '100%' }}
                        locale={esES}
                      />
                    </ConfigProvider>
                    {(dateRange || paymentMethod !== 'todos' || selectedQuarter !== 'todos' || selectedMonth !== 'todos' || search.trim()) && (
                      <Tooltip title="Limpiar todos los filtros">
                        <IconButton
                          onClick={clearAllFilters}
                          size="small"
                          sx={{
                            color: '#f759ab',
                            '&:hover': { backgroundColor: 'rgba(247, 89, 171, 0.1)' }
                          }}
                        >
                          <Clear />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Chips de filtros activos */}
              {(dateRange || paymentMethod !== 'todos' || selectedQuarter !== 'todos' || selectedMonth !== 'todos' || search.trim()) && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {dateRange && (
                    <Chip
                      label={`${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`}
                      onDelete={() => setDateRange(null)}
                      color="primary"
                      size="small"
                      sx={{ backgroundColor: '#f759ab' }}
                    />
                  )}
                  {selectedQuarter !== 'todos' && (
                    <Chip
                      label={`Trimestre: ${selectedQuarter}`}
                      onDelete={() => setSelectedQuarter('todos')}
                      color="primary"
                      size="small"
                      sx={{ backgroundColor: '#f759ab' }}
                    />
                  )}
                  {selectedMonth !== 'todos' && (
                    <Chip
                      label={`Mes: ${[
                        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
                      ][parseInt(selectedMonth, 10) - 1]}`}
                      onDelete={() => setSelectedMonth('todos')}
                      color="primary"
                      size="small"
                      sx={{ backgroundColor: '#f759ab' }}
                    />
                  )}
                  {paymentMethod !== 'todos' && (
                    <Chip
                      label={`Método: ${paymentMethod}`}
                      onDelete={() => setPaymentMethod('todos')}
                      color="primary"
                      size="small"
                      sx={{ backgroundColor: '#f759ab' }}
                    />
                  )}
                  {search.trim() && (
                    <Chip
                      label={`Búsqueda: ${search}`}
                      onDelete={() => setSearch('')}
                      color="primary"
                      size="small"
                      sx={{ backgroundColor: '#f759ab' }}
                    />
                  )}
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Métricas principales */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, border: '2px solid #22c55e', background: 'linear-gradient(135deg, #dcfce7, #bbf7d0)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#15803d', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                        {formatCurrency(summary.totalAmount)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total Anual
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 600 }}>
                        {summary.totalPayments} pagos
                      </Typography>
                    </Box>
                    <AttachMoney sx={{ fontSize: 40, color: '#22c55e' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, border: '2px solid #3b82f6', background: 'linear-gradient(135deg, #dbeafe, #bfdbfe)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#1d4ed8', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                        {formatCurrency(summary.monthlyAverage)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Promedio Mensual
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 600 }}>
                        {formatCurrency(summary.weeklyAverage)} semanal
                      </Typography>
                    </Box>
                    <ShowChart sx={{ fontSize: 40, color: '#3b82f6' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, border: '2px solid #8b5cf6', background: 'linear-gradient(135deg, #ede9fe, #ddd6fe)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#6b21a8', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                        {summary.bestMonth}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Mejor Mes
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#8b5cf6', fontWeight: 600 }}>
                        {formatCurrency(summary.bestMonthAmount)}
                      </Typography>
                    </Box>
                    <TrendingUp sx={{ fontSize: 40, color: '#8b5cf6' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, border: '2px solid #f97316', background: 'linear-gradient(135deg, #fed7aa, #fdba74)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#c2410c', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
                        {summary.bestQuarter}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Mejor Trimestre
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#f97316', fontWeight: 600 }}>
                        {formatCurrency(summary.bestQuarterAmount)}
                      </Typography>
                    </Box>
                    <BarChartIcon sx={{ fontSize: 40, color: '#f97316' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <Card sx={{ borderRadius: 3, border: '2px solid #06b6d4', background: 'linear-gradient(135deg, #cffafe, #a7f3d0)' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h4" sx={{ fontWeight: 700, color: '#0891b2', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                        {formatCurrency(summary.averagePayment)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Promedio por Pago
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#06b6d4', fontWeight: 600 }}>
                        Rango: {formatCurrency(summary.smallestPayment)} - {formatCurrency(summary.largestPayment)}
                      </Typography>
                    </Box>
                    <Assessment sx={{ fontSize: 40, color: '#06b6d4' }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Gráficos principales */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Análisis mensual */}
            <Grid item xs={12} lg={8}>
              <Card sx={{ borderRadius: 3, p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                  Análisis Mensual de Ingresos - {selectedYear}
                </Typography>
                <ResponsiveContainer width="100%" height={400}>
                  <ComposedChart data={chartData.monthly}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <RechartsTooltip
                      formatter={(value, name) => [
                        name === 'amount' ? formatCurrency(value) : 
                        name === 'average' ? formatCurrency(value) : value,
                        name === 'amount' ? 'Monto Total' : 
                        name === 'average' ? 'Promedio' : 'Cantidad'
                      ]}
                    />
                    <Legend />
                    <Area
                      yAxisId="left"
                      type="monotone"
                      dataKey="amount"
                      stroke="#f759ab"
                      fill="#f759ab"
                      fillOpacity={0.3}
                      name="Monto Total"
                    />
                    <Bar yAxisId="right" dataKey="count" fill="#22c55e" name="Cantidad de Pagos" />
                    <Line yAxisId="left" type="monotone" dataKey="average" stroke="#3b82f6" strokeWidth={3} name="Promedio por Pago" />
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>
            </Grid>

            {/* Análisis trimestral */}
            <Grid item xs={12} lg={4}>
              <Card sx={{ borderRadius: 3, p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                  Distribución Trimestral
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chartData.quarterly}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ quarter, percentage }) => `${quarter}: ${percentage.toFixed(1)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="amount"
                    >
                      {chartData.quarterly.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>
                
                <Box sx={{ mt: 2, borderTop: '1px solid #e0e7ff', pt: 2 }}>
                  {chartData.quarterly.map((entry, index) => (
                    <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2">{entry.quarter}:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#4b0082' }}>
                        {formatCurrency(entry.amount)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Card>
            </Grid>
          </Grid>

          {/* Gráficos secundarios */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            {/* Métodos de pago */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                  Métodos de Pago
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={chartData.methods}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ method, percentage }) => `${percentage.toFixed(1)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="amount"
                    >
                      {chartData.methods.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip formatter={(value) => formatCurrency(value)} />
                  </PieChart>
                </ResponsiveContainer>

                <Box sx={{ mt: 2, borderTop: '1px solid #e0e7ff', pt: 2 }}>
                  {chartData.methods.map((entry, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, backgroundColor: entry.color, borderRadius: '50%' }} />
                        <Typography variant="body2">{entry.method}</Typography>
                      </Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#4b0082' }}>
                        {formatCurrency(entry.amount)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Card>
            </Grid>

            {/* Top clientes */}
            <Grid item xs={12} md={6}>
              <Card sx={{ borderRadius: 3, p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                  Top 10 Clientes
                </Typography>
                {chartData.topClients && chartData.topClients.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData.topClients} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" tickFormatter={(value) => formatCurrency(value)} />
                      <YAxis 
                        dataKey="client" 
                        type="category" 
                        width={100} 
                        tick={{ fontSize: 12 }}
                        interval={0}
                      />
                      <RechartsTooltip 
                        formatter={(value, name) => [
                          formatCurrency(value),
                          name === 'amount' ? 'Monto Total' : name
                        ]}
                        labelFormatter={(label) => `Cliente: ${label}`}
                      />
                      <Bar dataKey="amount" fill="#f759ab" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: 300,
                    color: '#999'
                  }}>
                    <Typography variant="h6" sx={{ mb: 1 }}>
                      Sin datos disponibles
                    </Typography>
                    <Typography variant="body2">
                      No hay información de clientes para mostrar en el período seleccionado
                    </Typography>
                  </Box>
                )}
              </Card>
            </Grid>
          </Grid>

          {/* Días pico */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12}>
              <Card sx={{ borderRadius: 3, p: 3 }}>
                <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                  Top 5 Días con Mayor Actividad de Pagos
                </Typography>
                <Grid container spacing={2}>
                  {summary.peakDays.map((day, index) => (
                    <Grid item xs={12} sm={6} md={3} key={index}>
                      <Card sx={{ 
                        p: 2, 
                        background: `linear-gradient(135deg, ${COLORS[index]}20, ${COLORS[index]}10)`,
                        border: `1px solid ${COLORS[index]}40`
                      }}>
                        <Typography variant="h6" sx={{ color: COLORS[index], fontWeight: 700 }}>
                          #{index + 1}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {day.date}
                        </Typography>
                        <Typography variant="h6" sx={{ color: '#4b0082', fontWeight: 700 }}>
                          {formatCurrency(day.amount)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {day.count} pagos
                        </Typography>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Grid>
          </Grid>

          {/* Tabla de detalles */}
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, color: '#4b0082', fontWeight: 600 }}>
                Detalle de Pagos - {selectedYear} ({filteredPayments.length} registros)
                {(selectedQuarter !== 'todos' || selectedMonth !== 'todos' || dateRange) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {selectedQuarter !== 'todos' && `Trimestre: ${selectedQuarter} | `}
                    {selectedMonth !== 'todos' && `Mes: ${moment().month(parseInt(selectedMonth) - 1).format('MMMM')} | `}
                    {dateRange && `Período: ${dateRange[0].format('DD/MM/YYYY')} - ${dateRange[1].format('DD/MM/YYYY')}`}
                  </Typography>
                )}
              </Typography>

              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f8f0fc' }}>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Fecha</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Factura</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Cliente</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Método</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Monto</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Descripción</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Empleado</TableCell>
                      <TableCell sx={{ fontWeight: 600, color: '#4b0082' }}>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredPayments
                      .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      .map((payment, index) => (
                        <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f3e8ff' } }}>
                          <TableCell>
                            {payment.paymentDate
                              ? moment(payment.paymentDate).format('DD/MM/YYYY')
                              : 'N/A'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#7c3aed' }}>
                            {payment.invoiceNumber}
                          </TableCell>
                          <TableCell>{payment.clientName}</TableCell>
                          <TableCell>
                            <Chip
                              icon={payment.paymentMethod === 'efectivo' ? <AttachMoney /> :
                                    payment.paymentMethod === 'tarjeta' ? <CreditCard /> :
                                    <AccountBalance />}
                              label={payment.paymentMethod.toUpperCase()}
                              size="small"
                              variant="outlined"
                              sx={{
                                color: payment.paymentMethod === 'efectivo' ? '#22c55e' :
                                  payment.paymentMethod === 'tarjeta' ? '#3b82f6' : '#f97316',
                                borderColor: payment.paymentMethod === 'efectivo' ? '#22c55e' :
                                  payment.paymentMethod === 'tarjeta' ? '#3b82f6' : '#f97316'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#22c55e' }}>
                            {formatCurrency(payment.amount)}
                          </TableCell>
                          <TableCell>
                            {payment.description || '—'}
                          </TableCell>
                          <TableCell>
                            {payment.employeeName || 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Ver factura">
                              <IconButton
                                onClick={() => navigate(`/billing/invoice/${payment.invoiceID}`)}
                                sx={{ color: '#f759ab' }}
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={filteredPayments.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(event, newPage) => setPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setRowsPerPage(parseInt(event.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Filas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              />
            </CardContent>
          </Card>
        </Container>
      </div>
    </ConfigProvider>
  );
}

export default AnnualPaymentReport;
